import { useEffect, useState, useMemo } from "react";
import StockCard from "./stock.component";
import PageBackdrop from "../ui/PageBackdrop";
import PageHeader from "../ui/PageHeader";
import { useAuthFetch } from "../../services/clerk.service";

const CONVICTION_OPTIONS = ["Strong Buy", "Buy", "Hold", "Avoid"];

const SORT_OPTIONS = [
  {
    key: "weighted_score",
    label: "Quality Score",
    desc: true,
    path: (s) => s.analyst_report?.qualitative?.weighted_score,
  },
  {
    key: "upside_pct",
    label: "Upside %",
    desc: true,
    path: (s) => s.stock?.upside_pct,
  },
  {
    key: "current_price",
    label: "Price",
    desc: false,
    path: (s) => s.stock?.current_price,
  },
  {
    key: "free_cash_flow",
    label: "Free Cash Flow",
    desc: true,
    path: (s) => s.stock?.free_cash_flow,
  },
];

const DEFAULT_SORT = "weighted_score";
const DEFAULT_SORT_DESC = true;
const DEFAULT_SECTOR = "All";
const DEFAULT_CONVICTION = "All";
const DEFAULT_INDUSTRY = "All";
const PAGE_SIZE = 20;

const StockList = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeSort, setActiveSort] = useState(DEFAULT_SORT);
  const [sortDesc, setSortDesc] = useState(DEFAULT_SORT_DESC);
  const [activeSector, setActiveSector] = useState(DEFAULT_SECTOR);
  const [activeConviction, setActiveConviction] = useState(DEFAULT_CONVICTION);
  const [activeIndustry, setActiveIndustry] = useState("All");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [pipeline, setPipeline] = useState({});
  const [sectorsConfig, setSectorsConfig] = useState({
    sectors: [],
    industries: [],
  });

  const authFetch = useAuthFetch();

  useEffect(() => {
    const loadPipeline = async () => {
      try {
        const res = await authFetch("/pipeline/get-last-run");
        if (res.ok) setPipeline(await res.json());
      } catch {}
    };
    loadPipeline();
  }, [authFetch]);

  useEffect(() => {
    const loadSectors = async () => {
      try {
        const res = await authFetch("/sectors");
        if (res.ok) setSectorsConfig(await res.json());
      } catch {}
    };
    loadSectors();
  }, [authFetch]);

  useEffect(() => {
    const loadStocks = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await authFetch("/stocks");
        if (res.status === 202) {
          setStocks([]);
          return;
        }
        if (!res.ok) throw new Error("Failed to load stocks");
        const data = await res.json();
        if (!Array.isArray(data)) throw new Error("Invalid data format");
        setStocks(data);
      } catch (err) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };
    loadStocks();
  }, [authFetch]);

  const handleSort = (key, defaultDesc) => {
    if (activeSort === key) setSortDesc((d) => !d);
    else {
      setActiveSort(key);
      setSortDesc(defaultDesc);
    }
  };

  const isFiltered =
    activeSector !== DEFAULT_SECTOR ||
    activeIndustry !== DEFAULT_INDUSTRY ||
    activeConviction !== DEFAULT_CONVICTION ||
    activeSort !== DEFAULT_SORT;

  const clearFilters = () => {
    setActiveSector(DEFAULT_SECTOR);
    setActiveIndustry(DEFAULT_INDUSTRY);
    setActiveConviction(DEFAULT_CONVICTION);
    setActiveSort(DEFAULT_SORT);
    setSortDesc(DEFAULT_SORT_DESC);
  };

  const availableSectors = useMemo(() => {
    const fromData = new Set(
      stocks.map((s) => s.stock?.sector).filter(Boolean),
    );
    return ["All", ...sectorsConfig.sectors.filter((s) => fromData.has(s))];
  }, [stocks, sectorsConfig]);

  const availableIndustries = useMemo(() => {
    const filtered = stocks.filter(
      (s) => activeSector === "All" || s.stock?.sector === activeSector,
    );
    const fromData = [
      ...new Set(filtered.map((s) => s.stock?.industry).filter(Boolean)),
    ].sort();
    return ["All", ...fromData];
  }, [stocks, activeSector]);

  const filteredSorted = useMemo(() => {
    const opt = SORT_OPTIONS.find((o) => o.key === activeSort);
    const getSortValue = opt?.path ?? (() => 0);

    return [...stocks]
      .filter((s) => {
        const sector = s.stock?.sector;
        const conviction = s.analyst_report?.conviction;
        const industry = s.stock?.industry;
        if (activeSector !== "All" && sector !== activeSector) return false;
        if (activeIndustry !== "All" && industry !== activeIndustry)
          return false;
        if (activeConviction !== "All" && conviction !== activeConviction)
          return false;
        return true;
      })
      .sort((a, b) => {
        const av = getSortValue(a) ?? (sortDesc ? -Infinity : Infinity);
        const bv = getSortValue(b) ?? (sortDesc ? -Infinity : Infinity);
        return sortDesc ? bv - av : av - bv;
      });
  }, [
    stocks,
    activeSort,
    sortDesc,
    activeSector,
    activeIndustry,
    activeConviction,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const displayed = useMemo(
    () =>
      filteredSorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredSorted, safePage],
  );

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (safePage <= 4) return [1, 2, 3, 4, 5, "…", totalPages];
    if (safePage >= totalPages - 3) {
      return [
        1,
        "…",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [1, "…", safePage - 1, safePage, safePage + 1, "…", totalPages];
  }, [safePage, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [activeSort, sortDesc, activeSector, activeIndustry, activeConviction]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const convictionCounts = useMemo(() => {
    const counts = {};
    stocks.forEach((s) => {
      const c = s.analyst_report?.conviction;
      if (c) counts[c] = (counts[c] || 0) + 1;
    });
    return counts;
  }, [stocks]);

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .sl-root { background: var(--bg); min-height: 100vh; font-family: var(--font-sans); color: var(--text-primary); position: relative; }
        .sl-inner { position: relative; z-index: 1; max-width: 900px; margin: 0 auto; padding: 52px 24px 80px; }

        .sl-header { opacity: 0; animation: fadeUp 0.45s 0.05s ease forwards; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }

        .sl-controls { display: flex; flex-direction: column; gap: 0; margin-bottom: 24px; border: 1px solid var(--border-subtle); border-radius: var(--radius-md); background: var(--surface-1); overflow: hidden; opacity: 0; animation: fadeUp 0.45s 0.15s ease forwards; }
        .sl-controls-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid var(--border-subtle); }
        .sl-controls-left { display: flex; align-items: center; gap: 10px; }
        .sl-controls-title { font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.1em; }
        .sl-clear-btn { font-family: var(--font-mono); font-size: 12px; color: var(--accent); background: none; border: none; cursor: pointer; letter-spacing: 0.04em; opacity: 0.85; padding: 0; transition: opacity var(--dur-fast) var(--ease-out); }
        .sl-clear-btn:hover { opacity: 1; }
        .sl-close-btn { display: flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: var(--radius-sm); border: 1px solid var(--border-subtle); background: transparent; color: var(--text-tertiary); cursor: pointer; font-size: 12px; transition: all var(--dur-fast) var(--ease-out); flex-shrink: 0; line-height: 1; }
        .sl-close-btn:hover { border-color: var(--border-strong); color: var(--text-primary); background: rgba(255,255,255,0.03); }
        .sl-controls-body { display: flex; flex-direction: column; gap: 16px; padding: 16px; }
        .sl-control-row { display: flex; flex-direction: column; gap: 8px; }
        .sl-control-label { font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.1em; }

        .sl-chip-row { display: flex; gap: 6px; flex-wrap: wrap; }
        .sl-chip { font-family: var(--font-mono); font-size: 12px; padding: 6px 13px; border-radius: var(--radius-pill); border: 1px solid var(--border-subtle); background: transparent; color: var(--text-tertiary); cursor: pointer; letter-spacing: 0.03em; transition: all var(--dur-fast) var(--ease-out); white-space: nowrap; display: flex; align-items: center; gap: 6px; }
        .sl-chip:hover { border-color: var(--border-strong); color: var(--text-primary); background: rgba(255,255,255,0.03); }
        .sl-chip.active       { background: var(--accent-soft);   border-color: var(--accent-border);   color: var(--accent); }
        .sl-chip.active-buy   { background: var(--accent-soft);   border-color: var(--accent-border);   color: var(--accent); }
        .sl-chip.active-hold  { background: var(--neutral-soft);  border-color: rgba(251,191,36,0.28);  color: var(--neutral); }
        .sl-chip.active-avoid { background: var(--negative-soft); border-color: rgba(248,113,113,0.28); color: var(--negative); }
        .sl-chip-count { font-size: 12px; opacity: 0.55; }

        .sl-sort-row { display: flex; gap: 6px; flex-wrap: wrap; }
        .sl-sort-btn { display: inline-flex; align-items: center; gap: 5px; font-family: var(--font-sans); font-size: 13px; font-weight: 500; padding: 6px 13px; border-radius: var(--radius-sm); border: 1px solid var(--border-subtle); background: transparent; color: var(--text-tertiary); cursor: pointer; transition: all var(--dur-fast) var(--ease-out); white-space: nowrap; }
        .sl-sort-btn:hover { border-color: var(--border-strong); color: var(--text-primary); }
        .sl-sort-btn.active { background: var(--surface-2); border-color: var(--border-strong); color: var(--text-primary); }

        .sl-filters-pill { display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; border: 1px solid var(--border-subtle); border-radius: var(--radius-md); background: var(--surface-1); margin-bottom: 24px; cursor: pointer; transition: border-color var(--dur-fast) var(--ease-out); opacity: 0; animation: fadeUp 0.45s 0.15s ease forwards; }
        .sl-filters-pill:hover { border-color: var(--border-strong); }
        .sl-filters-pill-left { display: flex; align-items: center; gap: 10px; }
        .sl-filters-pill-label { font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.1em; }
        .sl-filter-tags { display: flex; gap: 5px; }
        .sl-filter-tag { font-family: var(--font-mono); font-size: 12px; padding: 2px 9px; border-radius: var(--radius-pill); background: var(--accent-soft); border: 1px solid var(--accent-border); color: var(--accent); letter-spacing: 0.03em; }
        .sl-filters-pill-open { font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); display: flex; align-items: center; gap: 4px; }

        .sl-results-bar { display: flex; align-items: center; justify-content: space-between; padding: 10px 0; border-top: 1px solid var(--border-subtle); border-bottom: 1px solid var(--border-subtle); margin-bottom: 16px; opacity: 0; animation: fadeUp 0.45s 0.27s ease forwards; }
        .sl-results-count { font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); letter-spacing: 0.05em; }
        .sl-results-count span { color: var(--accent); }
        .sl-updated { font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); letter-spacing: 0.04em; }

        .sl-cards { display: flex; flex-direction: column; gap: 8px; opacity: 0; animation: fadeUp 0.45s 0.33s ease forwards; }

        .sl-pagination { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 32px 0 8px; flex-wrap: wrap; opacity: 0; animation: fadeUp 0.45s 0.4s ease forwards; }
        .sl-page-btn { font-family: var(--font-mono); font-size: 12px; padding: 6px 14px; border-radius: var(--radius-pill); border: 1px solid var(--border-subtle); background: transparent; color: var(--text-tertiary); cursor: pointer; letter-spacing: 0.04em; transition: all var(--dur-fast) var(--ease-out); }
        .sl-page-btn:hover:not(:disabled) { border-color: var(--border-strong); color: var(--text-primary); background: rgba(255,255,255,0.03); }
        .sl-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }
        .sl-page-numbers { display: flex; align-items: center; gap: 4px; }
        .sl-page-num { font-family: var(--font-mono); font-size: 12px; min-width: 30px; height: 28px; padding: 0 8px; border-radius: var(--radius-sm); border: 1px solid var(--border-subtle); background: transparent; color: var(--text-tertiary); cursor: pointer; letter-spacing: 0.03em; transition: all var(--dur-fast) var(--ease-out); }
        .sl-page-num:hover { border-color: var(--border-strong); color: var(--text-primary); }
        .sl-page-num.active { background: var(--accent-soft); border-color: var(--accent-border); color: var(--accent); }
        .sl-page-ellipsis { font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); padding: 0 4px; user-select: none; }

        .sl-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 40vh; gap: 14px; }
        .sl-spinner { width: 26px; height: 26px; border: 2px solid var(--border-subtle); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.7s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .sl-spinner-label { font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); letter-spacing: 0.08em; text-transform: uppercase; }
        .sl-error { background: var(--negative-soft); border: 1px solid rgba(248,113,113,0.28); border-radius: var(--radius-md); padding: 16px 20px; color: var(--negative); font-size: 14px; }
        .sl-empty { text-align: center; padding: 72px 0; color: var(--text-tertiary); font-size: 14px; font-family: var(--font-mono); letter-spacing: 0.05em; }

        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }

        .sl-select { appearance: none; background: transparent; border: 1px solid var(--border-subtle); border-radius: var(--radius-pill); padding: 6px 32px 6px 13px; font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); cursor: pointer; letter-spacing: 0.03em; transition: all var(--dur-fast) var(--ease-out); outline: none; background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394a3b8' stroke-width='1.4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; }
        .sl-select:hover { border-color: var(--border-strong); color: var(--text-primary); }
        .sl-select.active { background-color: var(--accent-soft); border-color: var(--accent-border); color: var(--accent); }
        .sl-select option { background: var(--bg); color: var(--text-primary); }

        @media (max-width: 600px) {
          .sl-inner { padding: 36px 16px 60px; }
          .sl-results-bar { flex-direction: column; align-items: flex-start; gap: 4px; }
        }
      `}</style>

      <div className="sl-root">
        <PageBackdrop />

        <div className="sl-inner">
          <div className="sl-header">
            <PageHeader
              eyebrow="Updated daily"
              title={
                <>
                  The <em>best</em> businesses
                  <br />
                  trading at a discount.
                </>
              }
              description="Ranked by our valuation and quality scoring models."
            />
          </div>

          {/* Filters */}
          {!loading &&
            !error &&
            stocks.length > 0 &&
            (filtersOpen ? (
              <div className="sl-controls">
                <div className="sl-controls-header">
                  <div className="sl-controls-left">
                    <span className="sl-controls-title">Filters</span>
                    {isFiltered && (
                      <button className="sl-clear-btn" onClick={clearFilters}>
                        ↺ Clear
                      </button>
                    )}
                  </div>
                  <button
                    className="sl-close-btn"
                    onClick={() => setFiltersOpen(false)}
                  >
                    ✕
                  </button>
                </div>
                <div className="sl-controls-body">
                  {/* Conviction */}
                  <div className="sl-control-row">
                    <div className="sl-control-label">Conviction</div>
                    <div className="sl-chip-row">
                      <button
                        className={`sl-chip${activeConviction === "All" ? " active" : ""}`}
                        onClick={() => setActiveConviction("All")}
                      >
                        All
                      </button>
                      {CONVICTION_OPTIONS.map((c) => {
                        const activeClass =
                          activeConviction === c
                            ? c === "Avoid"
                              ? " active-avoid"
                              : c === "Hold"
                                ? " active-hold"
                                : " active-buy"
                            : "";
                        return (
                          <button
                            key={c}
                            className={`sl-chip${activeClass}`}
                            onClick={() => setActiveConviction(c)}
                          >
                            {c}
                            {convictionCounts[c] != null && (
                              <span className="sl-chip-count">
                                {convictionCounts[c]}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sector */}
                  {availableSectors.length > 1 && (
                    <div className="sl-control-row">
                      <div className="sl-control-label">Sector</div>
                      <div className="sl-chip-row">
                        {availableSectors.map((sector) => (
                          <button
                            key={sector}
                            className={`sl-chip${activeSector === sector ? " active" : ""}`}
                            onClick={() => {
                              setActiveSector(sector);
                              setActiveIndustry("All");
                            }}
                          >
                            {sector}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Industry */}
                  {availableIndustries.length > 1 && (
                    <div className="sl-control-row">
                      <div className="sl-control-label">Industry</div>
                      <select
                        className={`sl-select${activeIndustry !== "All" ? " active" : ""}`}
                        value={activeIndustry}
                        onChange={(e) => setActiveIndustry(e.target.value)}
                        style={{ width: "fit-content", maxWidth: "100%" }}
                      >
                        {availableIndustries.map((ind) => (
                          <option key={ind} value={ind}>
                            {ind}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Sort */}
                  <div className="sl-control-row">
                    <div className="sl-control-label">Sort by</div>
                    <div className="sl-sort-row">
                      {SORT_OPTIONS.map(({ key, label, desc }) => (
                        <button
                          key={key}
                          className={`sl-sort-btn${activeSort === key ? " active" : ""}`}
                          onClick={() => handleSort(key, desc)}
                        >
                          {label}
                          {activeSort === key && (
                            <span style={{ fontSize: 12, opacity: 0.5 }}>
                              {sortDesc ? "↓" : "↑"}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="sl-filters-pill"
                onClick={() => setFiltersOpen(true)}
              >
                <div className="sl-filters-pill-left">
                  <span className="sl-filters-pill-label">Filters</span>
                  {isFiltered && (
                    <div className="sl-filter-tags">
                      {activeConviction !== DEFAULT_CONVICTION && (
                        <span className="sl-filter-tag">
                          {activeConviction}
                        </span>
                      )}
                      {activeSector !== DEFAULT_SECTOR && (
                        <span className="sl-filter-tag">{activeSector}</span>
                      )}
                      {activeIndustry !== DEFAULT_INDUSTRY && (
                        <span className="sl-filter-tag">{activeIndustry}</span>
                      )}
                      {activeSort !== DEFAULT_SORT && (
                        <span className="sl-filter-tag">
                          {
                            SORT_OPTIONS.find((o) => o.key === activeSort)
                              ?.label
                          }
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <span className="sl-filters-pill-open">
                  Open
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                    <path
                      d="M2 4l3.5 3.5L9 4"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            ))}

          {/* Results bar */}
          {!loading && !error && stocks.length > 0 && (
            <div className="sl-results-bar">
              <span className="sl-results-count">
                {filteredSorted.length === 0 ? (
                  <>
                    No matches in <span>{stocks.length}</span> stocks
                  </>
                ) : (
                  <>
                    Showing{" "}
                    <span>
                      {(safePage - 1) * PAGE_SIZE + 1}–
                      {Math.min(safePage * PAGE_SIZE, filteredSorted.length)}
                    </span>{" "}
                    of {filteredSorted.length}
                    {filteredSorted.length !== stocks.length
                      ? ` (${stocks.length} total)`
                      : " stocks"}
                  </>
                )}
              </span>
              <span className="sl-updated">
                {pipeline.finished_at
                  ? new Date(pipeline.finished_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : ""}
              </span>
            </div>
          )}

          {loading && (
            <div className="sl-loading">
              <div className="sl-spinner" />
              <span className="sl-spinner-label">Scanning the market…</span>
            </div>
          )}

          {!loading && error && <div className="sl-error">{error}</div>}

          {!loading && !error && stocks.length === 0 && (
            <div className="sl-empty">
              No stocks available yet — check back soon.
            </div>
          )}

          {!loading && !error && displayed.length > 0 && (
            <div className="sl-cards">
              {displayed.map((stock) => (
                <StockCard key={stock.stock?.ticker} stock={stock} />
              ))}
            </div>
          )}

          {!loading && !error && totalPages > 1 && (
            <div className="sl-pagination">
              <button
                className="sl-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
              >
                ← Prev
              </button>
              <div className="sl-page-numbers">
                {pageNumbers.map((p, i) =>
                  p === "…" ? (
                    <span
                      key={`ellipsis-${i}`}
                      className="sl-page-ellipsis"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      className={`sl-page-num${p === safePage ? " active" : ""}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  ),
                )}
              </div>
              <button
                className="sl-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StockList;
